import type {
  RecurringManualArrival,
  RecurringManualMode,
  RecurringManualSchedule,
} from '@/types/job';

/**
 * Scheduling v2 — a recurring series the office schedules by hand. Every
 * visit keeps the day, arrival and technicians chosen here; nothing searches
 * for a slot or a technician. The API stores it as
 * `recurringConfig.manualSchedule` (see the backend's
 * `recurring-manual-schedule.ts` for the same rules).
 */
export interface RecurringManualScheduleDraft {
  mode: RecurringManualMode;
  arrival: RecurringManualArrival;
  /** Local HH:mm — only read for the WINDOW arrival. */
  windowStart: string;
  windowEnd: string;
}

export const DEFAULT_RECURRING_MANUAL_SCHEDULE: RecurringManualScheduleDraft = {
  mode: 'FIXED_DAY',
  arrival: 'EXACT',
  windowStart: '08:00',
  windowEnd: '12:00',
};

/**
 * Only an exact-time series carries a visit time. A window visit gets its time
 * when the office schedules that visit.
 */
export function recurringManualNeedsTime(draft: RecurringManualScheduleDraft): boolean {
  return draft.mode === 'FIXED_DAY' && draft.arrival === 'EXACT';
}

/**
 * The first thing that stops the series from being saved, or null. Technicians
 * are optional: a series without them creates each visit unassigned.
 */
export function recurringManualScheduleProblem(
  draft: RecurringManualScheduleDraft,
  input: { startTime: string; endTime: string },
): string | null {
  if (recurringManualNeedsTime(draft)) {
    if (!input.startTime || !input.endTime) return 'Set when each visit starts and ends.';
    if (input.endTime <= input.startTime) return 'Each visit must end after it starts.';
  }
  if (draft.mode === 'FIXED_DAY' && draft.arrival === 'WINDOW') {
    if (!draft.windowStart || !draft.windowEnd || draft.windowEnd <= draft.windowStart) {
      return 'The arrival window must end after it starts.';
    }
  }
  return null;
}

/** What the API stores for the draft and the chosen technicians. */
export function toRecurringManualSchedule(
  draft: RecurringManualScheduleDraft,
  technicianIds: string[],
): RecurringManualSchedule {
  return {
    mode: draft.mode,
    ...(draft.mode === 'FIXED_DAY' ? { arrival: draft.arrival } : {}),
    ...(draft.mode === 'FIXED_DAY' && draft.arrival === 'WINDOW'
      ? { windowStart: draft.windowStart, windowEnd: draft.windowEnd }
      : {}),
    ...(technicianIds.length > 0 ? { technicianIds } : {}),
  };
}
